import { useCallback } from 'react';

import type HighchartsType from 'highcharts/highstock';
import { useTheme } from '@/shared/ui';

import { GX_TEXT } from '@/config/colors';

import { CHART_GRID_COLOR } from '@/features/_shared/chart/hooks/options/useChartFunctions';
import type { TimeSeriesPointCustom } from '@/features/_shared/chart/lib/buildChartLineSeries';
import { createTooltip } from '@/features/_shared/chart/lib/createTooltip';
import { DateTextVariants, useDateText } from '@/features/_shared/user';

const ROLLOVER_TOOLTIP_BACKGROUND = CHART_GRID_COLOR;

const ROLLOVER_TOOLTIP_STYLES = {
  bodyRow:
    'font-size: 10px; font-style: normal;font-weight: 500;justify-content: space-between;height: 19px; display: flex;',
  container: 'display: flex; flex-direction: column; width:350px;',
  dateColumn: 'width: 65px; padding-left: 8px; border-left: 1px solid gray;',
  headerRow: 'font-size: 8px; display: flex; font-weight: 400;justify-content: space-between;',
  nameColumn:
    'width: 152px; padding-right: 8px; border-right: 1px solid gray; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;',
  periodColumn: 'width: 65px; padding-left: 8px; padding-right: 8px;',
  valueBox:
    'font-size:10px; line-height:13px; height:14px; width:55px; padding-left:4px; padding-right:4px; text-align: start;',
  valueContainer: 'height 19px; padding-left:8px; padding-top:3px'
} as const;

const ROLLOVER_TOOLTIP_COLUMN_WIDTHS = { date: 65, name: 152, period: 65 } as const;

type FullScreenRolloverFormatter = (
  allChartSeries: HighchartsType.Series[],
  hoveredPointXValue: string | number | undefined
) => string;

const createTooltipColumn = (content: string, style: string): string =>
  `<div style="${style}">${content}</div>`;

const createTooltipValueBox = ({
  backgroundColor,
  textColor,
  value
}: {
  backgroundColor: HighchartsType.ColorType;
  textColor: string;
  value: string;
}): string =>
  `<div style="background-color:${backgroundColor}; color:${textColor}; ${ROLLOVER_TOOLTIP_STYLES.valueBox}">${value}</div>`;

const createRolloverTooltipHeader = (): string => {
  const columns = [
    { content: 'Short Title', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.name}px;` },
    { content: 'PeriodAbs', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.period}px;` },
    { content: 'Last date', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.date}px;` },
    { content: '', style: 'width: 10%;' }
  ]
    .map(({ content, style }) => createTooltipColumn(content, style))
    .join('');
  return `<div style="${ROLLOVER_TOOLTIP_STYLES.container}"><div style="${ROLLOVER_TOOLTIP_STYLES.headerRow}">${columns}</div>`;
};

const createRolloverTooltipBodyRow = ({
  custom,
  formatDateString,
  point,
  valueTextColor,
  value
}: {
  custom: TimeSeriesPointCustom | undefined;
  formatDateString: (date: string, variant: DateTextVariants) => string;
  point: HighchartsType.Point;
  valueTextColor: string;
  value: string;
}): string => {
  const nameColumn = createTooltipColumn(point.series.name, ROLLOVER_TOOLTIP_STYLES.nameColumn);
  const periodColumn = createTooltipColumn(
    custom?.periodAbs ?? '',
    ROLLOVER_TOOLTIP_STYLES.periodColumn
  );
  const date = formatDateString(new Date(point.x).toJSON(), DateTextVariants.FULL);
  const dateColumn = createTooltipColumn(date, ROLLOVER_TOOLTIP_STYLES.dateColumn);
  const valueBox = createTooltipValueBox({
    backgroundColor: point.series.color ?? GX_TEXT,
    textColor: valueTextColor,
    value
  });
  const valueContainer = createTooltipColumn(valueBox, ROLLOVER_TOOLTIP_STYLES.valueContainer);
  return `<div style="${ROLLOVER_TOOLTIP_STYLES.bodyRow}">${nameColumn}${periodColumn}${dateColumn}${valueContainer}</div>`;
};

export const useFullScreenRolloverFormatter = (): FullScreenRolloverFormatter => {
  const theme = useTheme();
  const { formatDateString } = useDateText();
  const valueTextColor = theme.palette.gx.mainBackground;

  return useCallback(
    (allChartSeries, hoveredPointXValue) =>
      createTooltip({
        allChartSeries,
        hoveredPointXValue,
        createHeader: createRolloverTooltipHeader,
        createBodyRow: (point, custom, value) =>
          createRolloverTooltipBodyRow({ custom, formatDateString, point, valueTextColor, value })
      }),
    [formatDateString, valueTextColor]
  );
};

export const createFullScreenRolloverTooltip = (
  createTooltipFormatter: FullScreenRolloverFormatter,
  textColor = GX_TEXT
): HighchartsType.TooltipOptions => ({
  backgroundColor: ROLLOVER_TOOLTIP_BACKGROUND,
  borderRadius: 0,
  className: 'tooltip-fullscreen-chart',
  enabled: true,
  followPointer: true,
  formatter(): string {
    return createTooltipFormatter(this.series.chart.series, this.x);
  },
  positioner() {
    return { x: 100, y: 80 };
  },
  shape: 'rect',
  split: false,
  style: {
    color: textColor,
    fontFamily: 'Cairo, sans-serif',
    zIndex: 1303
  },
  useHTML: true
});
